import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiGet } from '@/hooks/useApiQuery';
import { useAuthReady } from '@/hooks/useAuthReady';
import { isClientRole } from '@/utils/roleConstants';
import { post } from '@/utils/apiClient';

export interface ActivityItem {
  id: string;
  source: 'care_message' | 'team_message' | 'task' | 'note' | 'scheduling' | 'billing' | 'system';
  source_id: string;
  item_type: 'action_required' | 'informational';
  status: 'active' | 'resolved' | 'expired';
  read_state: 'unread' | 'read' | 'dismissed' | 'snoozed';
  priority: 'low' | 'medium' | 'high' | 'urgent' | null;
  title: string;
  message: string | null;
  deep_link: string | null;
  link_params: Record<string, unknown> | null;
  due_at: string | null;
  snooze_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActivityFeedResponse {
  items: ActivityItem[];
  unread_count: number;
  total: number;
  limit: number;
  offset: number;
}

export interface UnreadCountResponse {
  unread_count: number;
}

export const ACTIVITY_FEED_QUERY_KEY = ['activity-center', 'feed'] as const;
export const ACTIVITY_UNREAD_QUERY_KEY = ['activity-center', 'unread'] as const;

function isEnabled(ready: boolean, user: { role?: string; roles?: string[] } | false | null | undefined) {
  if (!ready || !user) return false;
  const roles = user.roles || (user.role ? [user.role] : []);
  return !isClientRole(roles);
}

export function useActivityFeed(activeOnly = true) {
  const { ready, user } = useAuthReady();
  return useApiGet<ActivityFeedResponse>(
    isEnabled(ready, user) ? ACTIVITY_FEED_QUERY_KEY : null,
    `/api/activity-center/feed?active_only=${activeOnly}`,
    {
      staleTime: 30 * 1000,
      refetchInterval: 30 * 1000,
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: false,
      retry: false,
    },
  );
}

export function useActivityUnreadCount() {
  const { ready, user } = useAuthReady();
  return useApiGet<UnreadCountResponse>(
    isEnabled(ready, user) ? ACTIVITY_UNREAD_QUERY_KEY : null,
    '/api/activity-center/unread-count',
    {
      staleTime: 30 * 1000,
      refetchInterval: 30 * 1000,
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: false,
      retry: false,
    },
  );
}

async function postItem<T>(endpoint: string, body?: unknown): Promise<T> {
  const res = await post(endpoint, body ?? {});
  if (!res.ok) {
    const bodyText = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${bodyText || res.statusText}`);
  }
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (null as T);
}

export function useActivityCenterActions() {
  const qc = useQueryClient();

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ACTIVITY_FEED_QUERY_KEY });
    await qc.invalidateQueries({ queryKey: ACTIVITY_UNREAD_QUERY_KEY });
  };

  const markRead = useMutation<ActivityItem, Error, string>({
    mutationFn: (itemId) => postItem<ActivityItem>(`/api/activity-center/${itemId}/read`),
    onSuccess: invalidate,
  });

  const markUnread = useMutation<ActivityItem, Error, string>({
    mutationFn: (itemId) => postItem<ActivityItem>(`/api/activity-center/${itemId}/unread`),
    onSuccess: invalidate,
  });

  const dismiss = useMutation<ActivityItem, Error, string>({
    mutationFn: (itemId) => postItem<ActivityItem>(`/api/activity-center/${itemId}/dismiss`),
    onSuccess: invalidate,
  });

  const snooze = useMutation<ActivityItem, Error, { itemId: string; until: string }>({
    mutationFn: ({ itemId, until }) =>
      postItem<ActivityItem>(`/api/activity-center/${itemId}/snooze`, { until } as never),
    onSuccess: invalidate,
  });

  return {
    markRead: markRead.mutateAsync,
    markUnread: markUnread.mutateAsync,
    dismiss: dismiss.mutateAsync,
    snooze: snooze.mutateAsync,
    isPending:
      markRead.isPending || markUnread.isPending || dismiss.isPending || snooze.isPending,
  };
}
